import { InsightsCapabilities } from '@pushly/insights-sdk/lib/insights-capabilities'
import { DashboardTemplate, HeadlineTileSpec, primaryRankingMeasureFor } from '../template'

export type FieldLookup = Pick<InsightsCapabilities, 'getField'>

export type RegistryCapabilities = { notifications?: FieldLookup; prompts?: FieldLookup }

/**
 * Tiles whose figure is derived have no field of their own in the definitions, so entitlement to them is
 * entitlement to the measure they are derived from.
 */
const ENTITLING_MEASURE_BY_TILE_KEY: Record<string, string> = { revenue_per_send: 'purchase_amount' }

/*
 * A widget is withheld on its own measure only, never on the measures it is derived from (impressions for CTR, say),
 * and table columns are not gated. The definitions withhold whole measures, and a derived figure whose inputs are
 * withheld already reads as unavailable from the series.
 */

/**
 * Whether the definitions are loaded and leave a measure out. The definitions list only the measures the
 * organization may see, so a missing one is withheld; definitions that have not loaded withhold nothing.
 */
export const isMeasureWithheld = (capabilities: FieldLookup | undefined, measure: string): boolean =>
    capabilities !== undefined && capabilities.getField(measure) === undefined

/**
 * The measures, in the order given, that loaded definitions list. Definitions that have not loaded say nothing
 * about what is offered, so nothing is: a read built from this must wait for them.
 */
export const offeredMeasures = (capabilities: FieldLookup | undefined, measures: string[]): string[] =>
    capabilities ? measures.filter((measure) => capabilities.getField(measure) !== undefined) : []

/** The tile's measure and which definitions list it. The subscriber total comes from history, not from the definitions. */
const entitlementFor = (
    spec: HeadlineTileSpec,
    registry: RegistryCapabilities,
): { capabilities: FieldLookup | undefined; measure: string } | undefined =>
    spec.entity === 'subscribers'
        ? undefined
        : {
              capabilities: registry[spec.entity],
              measure: ENTITLING_MEASURE_BY_TILE_KEY[spec.key] ?? spec.measure,
          }

export const isHeadlineTileWithheld = (spec: HeadlineTileSpec, registry: RegistryCapabilities): boolean => {
    const entitlement = entitlementFor(spec, registry)
    return entitlement !== undefined && isMeasureWithheld(entitlement.capabilities, entitlement.measure)
}

/** Top Notifications cannot be drawn without the measure it ranks by. */
export const isTopNotificationsWithheld = (
    template: DashboardTemplate,
    mixedCurrency: boolean,
    registry: RegistryCapabilities,
): boolean => isMeasureWithheld(registry.notifications, primaryRankingMeasureFor(template, mixedCurrency))
